import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { onRelayPhase, RelayPhaseEvent } from "../services/relay.service";

// Global status surface for the gasless lane (signing transparency, V9.1 UX spec §7):
// typed-data prompts are opaque in MetaMask, so while one is pending this overlay states
// exactly what is being signed — the human-readable action label every relay call site
// provides — and that it costs no gas. Success flashes briefly and auto-dismisses;
// errors persist until closed (screens also render their own decoded revert reason, so
// this is a secondary, app-wide surface). Wallet cancellations dismiss silently.

const PHASE_COPY: Record<string, string> = {
  signing: "Check your wallet — approve the signature. No gas, no BNB.",
  relaying: "Submitting to the relay…",
  confirming: "Waiting for on-chain confirmation…",
};

const SUCCESS_HIDE_MS = 4000;

const RelayStatusOverlay = () => {
  const [event, setEvent] = useState<RelayPhaseEvent | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsubscribe = onRelayPhase((e) => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      if (e.phase === "cancelled") {
        setEvent(null);
        return;
      }
      setEvent(e);
      if (e.phase === "success") {
        hideTimer.current = setTimeout(() => setEvent(null), SUCCESS_HIDE_MS);
      }
    });
    return () => {
      unsubscribe();
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
      }
    };
  }, []);

  if (!event) {
    return null;
  }

  const pending = event.phase === "signing" || event.phase === "relaying" || event.phase === "confirming";

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: "16px", sm: "24px" },
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        maxWidth: "min(92vw, 480px)",
        backgroundColor: "white",
        border: "1px solid #e0e0e0",
        borderRadius: "16px",
        boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
        padding: "12px 16px",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {pending && <CircularProgress size={20} sx={{ color: "black", flexShrink: 0 }} />}
      {event.phase === "success" && <CheckCircleOutlineIcon sx={{ color: "#1B5E20", flexShrink: 0 }} />}
      {event.phase === "error" && <ErrorOutlineIcon sx={{ color: "#B00020", flexShrink: 0 }} />}

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "14px", fontWeight: 700, color: "black" }}>
          {event.label ?? "Gasless action"}
        </Box>
        <Box sx={{ fontSize: "13px", color: event.phase === "error" ? "#B00020" : "#555", overflowWrap: "break-word" }}>
          {event.phase === "success"
            ? "Confirmed on-chain."
            : event.phase === "error"
              ? event.error ?? "The action failed."
              : PHASE_COPY[event.phase]}
        </Box>
      </Box>

      {event.phase === "error" && (
        <IconButton size="small" onClick={() => setEvent(null)} sx={{ flexShrink: 0 }} aria-label="Dismiss">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default RelayStatusOverlay;
